/**
 * ExportDialog.jsx — Carousel Composer export sheet. Every slide gets a row and
 * a status, so a cancelled or failed file skips that one file instead of ending
 * the run, and you can see exactly what saved and retry the rest.
 *
 * The rows are the last look before the files are written, so they ask their
 * stages for `graded`: a non-interactive stage otherwise falls back to the 480px
 * thumbnail and the CSS approximation, which is off by a mean of ~58/255 on a
 * strong grade. The one screen whose job is "confirm what you are about to save"
 * must not be the least accurate preview in the app.
 */

import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@mui/material';
import {
  closeExport, pickFolder, runExport, selectImgById, setExportOpts, stopExport, useCarousel,
} from '../../hooks/useCarouselStore';
import { useMobile } from '../../hooks/useMobile';
import { layoutName } from '../../lib/carousel/geometry.js';
import {
  EXPORT_LABELS, canPickFolder, exportNoteText, outDims, sanitizePrefix, saveButtonLabel,
  slideFilename, slideHasPhotos,
} from '../../lib/carousel/exportRender.js';
import SlideStage, { useSlidePreviews } from './SlideStage';

export default function ExportDialog() {
  const { isMobile } = useMobile();
  const open = useCarousel((s) => s.exportOpen);
  const slides = useCarousel((s) => s.slides);
  const panos = useCarousel((s) => s.panos);
  const design = useCarousel((s) => s.design);
  const exportOpts = useCarousel((s) => s.exportOpts);
  const status = useCarousel((s) => s.exportStatus);
  const running = useCarousel((s) => s.exportRunning);
  const stopping = useCarousel((s) => s.exportStop);
  const imgById = useCarousel(selectImgById);
  const outDir = useCarousel((s) => s.outDir);
  const runRef = useRef(null);
  const [prefixRaw, setPrefixRaw] = useState(exportOpts.prefix);
  const pv = useSlidePreviews(slides, panos, imgById, open);

  useEffect(() => {
    setPrefixRaw((r) => (sanitizePrefix(r) === exportOpts.prefix ? r : exportOpts.prefix));
  }, [exportOpts.prefix]);

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => { const r = runRef.current; if (r && !r.disabled) r.focus(); }, 30);
    return () => clearTimeout(id);
  }, [open]);

  const stateOf = (si) => {
    const sl = slides[si];
    if (!slideHasPhotos(sl, panos, imgById)) return 'empty';
    return status[sl.id] || 'ready';
  };
  const states = slides.map((_, si) => stateOf(si));
  const total = states.filter((st) => st !== 'empty').length;
  const savedN = states.filter((st) => st === 'saved').length;
  const pending = states.map((st, i) => (st !== 'saved' && st !== 'empty' ? i : -1)).filter((i) => i >= 0);

  const asZip = exportOpts.bundle === 'zip';
  const runLabel = running
    ? (stopping ? 'Stopping…' : 'Stop after this one')
    : !total ? 'Nothing to save' : !pending.length ? 'All saved'
      : (savedN ? 'Save remaining ' + pending.length : 'Save all ' + pending.length) + (asZip ? ' as zip' : '');

  return (
    <Dialog
      open={open}
      onClose={() => { if (!running) closeExport(); }}
      fullScreen={isMobile}
      className="cc-modal"
      aria-labelledby="cc-export-title"
      slotProps={{
        paper: { className: 'cc-root export-sheet cc-export' + (isMobile ? ' is-phone' : '') },
        backdrop: { style: { background: 'rgba(12,12,14,.66)' } },
      }}
    >
      <div className="sheet-head">
        <h3 id="cc-export-title">Export carousel</h3>
        <span className="hint">
          {total ? savedN + ' of ' + total + ' saved · ' + outDims(design, exportOpts) + ' ' + (exportOpts.type === 'png' ? 'PNG' : 'JPEG') : ''}
        </span>
        <button type="button" className="btn tiny ghost" onClick={closeExport}>Close</button>
      </div>
      <div className="export-opts">
        <label className="ctl">
          <span className="lbl">Size</span>
          <select value={String(exportOpts.qual)} disabled={running} onChange={(e) => setExportOpts({ qual: +e.target.value })}>
            <option value="1080">1080 px</option>
            <option value="1440">1440 px · Instagram max</option>
            <option value="2160">2160 px</option>
          </select>
        </label>
        <label className="ctl">
          <span className="lbl">File</span>
          <select value={exportOpts.type} disabled={running} onChange={(e) => setExportOpts({ type: e.target.value })}>
            <option value="jpg">JPEG</option>
            <option value="png">PNG · lossless</option>
          </select>
        </label>
        <label className="ctl">
          <span className="lbl">Deliver</span>
          <div className="seg">
            <button
              type="button"
              aria-pressed={exportOpts.bundle !== 'zip'}
              disabled={running}
              onClick={() => setExportOpts({ bundle: 'files' })}
            >
              Files
            </button>
            <button
              type="button"
              aria-pressed={exportOpts.bundle === 'zip'}
              disabled={running}
              onClick={() => setExportOpts({ bundle: 'zip' })}
            >
              One zip
            </button>
          </div>
        </label>
        <label className="ctl">
          <span className="lbl">Name</span>
          <input
            type="text"
            className="prefix"
            value={prefixRaw}
            disabled={running}
            onChange={(e) => { setPrefixRaw(e.target.value); setExportOpts({ prefix: sanitizePrefix(e.target.value) }); }}
          />
        </label>
        {canPickFolder() && (
          <button type="button" className="btn tiny ghost" onClick={pickFolder}>Choose output folder</button>
        )}
      </div>
      <ol className="export-list">
        {slides.map((sl, si) => {
          const st = states[si];
          return (
            <li key={sl.id} className="xrow" data-si={si}>
              <span className="xnum mono">{String(si + 1).padStart(2, '0')}</span>
              <span className="xthumb">
                <SlideStage
                  sl={sl}
                  si={si}
                  design={design}
                  group={sl.pano ? panos[sl.pano.gid] : null}
                  imgById={imgById}
                  interactive={false}
                  graded
                  previews={pv}
                />
              </span>
              <div className="xinfo">
                <span className="xname">{slideFilename(si, exportOpts)}</span>
                <span className="xmeta lbl">{layoutName(sl, panos)}</span>
              </div>
              <span className="xstatus" data-state={st}>{EXPORT_LABELS[st]}</span>
              <button
                type="button"
                className="btn tiny ghost xsave"
                disabled={running || st === 'empty'}
                onClick={() => runExport([si], { forceFiles: true })}
              >
                {saveButtonLabel(st)}
              </button>
            </li>
          );
        })}
      </ol>
      <div className="export-foot">
        <span className="hint">{exportNoteText({ current: outDir }, exportOpts)}</span>
        <button
          ref={runRef}
          type="button"
          className="btn primary"
          disabled={!running && !pending.length}
          onClick={() => { if (running) stopExport(); else runExport(pending, { logRun: true }); }}
        >
          {runLabel}
        </button>
      </div>
    </Dialog>
  );
}
